import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { ManageApis } from "../../AppServices/ManageApi";
import { Message } from "@arco-design/web-react";
import { HEADLESS_APP_LABELS } from "./headlessAppHitLogsConfig";
import { getLocalStorage } from "../../Functions/Utils";
import { useStoreValues } from "../../Context/storeProvider";

const buildQueryParams = (params) => {
  const cleaned = { ...params };
  Object.keys(cleaned).forEach(
    (key) =>
      (cleaned[key] == null || cleaned[key] === "") && delete cleaned[key]
  );
  return new URLSearchParams(cleaned).toString();
};

const applyClientId = (filters = {}, userRole) => {
  const updatedFilters = { ...filters };

  if (userRole === "Client" && !updatedFilters.clientId) {
    const storedclientId = getLocalStorage("accountsId");
    if (storedclientId) {
      updatedFilters.clientId = storedclientId;
    }
  }

  return updatedFilters;
};

export function useHeadlessAppHitLogs() {
  const { user_Roll } = useStoreValues();
  const [deviceList, setDeviceList] = useState([]);
  const [accountList, setAccountList] = useState([]);
  const [sourceList, setSourceList] = useState([]);
  const [docTypeList, setDocTypeList] = useState([]);
  const [statusList, setStatusList] = useState([]);
  const [tableData, setTableData] = useState([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  const fetchDeviceList = useCallback(async () => {
    try {
      const response = await ManageApis("get", global.getHeadlessAppDeviceList);

      if (response.message === "Success") {
        const devices =
          response?.response_data?.data?.devices?.map((item) => ({
            key: item.deviceId,
            value: item.deviceId,
          })) || [];
        setDeviceList(devices);
      } else {
        Message.error(
          response?.message || HEADLESS_APP_LABELS?.errorDeviceFetch,
        );
      }
    } catch (error) {
      console.error(HEADLESS_APP_LABELS?.errorDeviceFetch2, error);
    }
  }, []);

  const getAccountList = useCallback(async () => {
    try {
      const response = await ManageApis("get", global.getHeadlessClientsList);
      if (response.message === "Success") {
        const accounts =
          response?.response_data?.map((item) => ({
            key: item?.clientId,
            value: item?.clientName,
          })) || [];
        setAccountList(accounts);
      } else {
        Message.error(
          response?.message || HEADLESS_APP_LABELS?.errorAccountFetch,
        );
      }
    } catch (error) {
      console.error(HEADLESS_APP_LABELS?.errorAccountFetch2, error);
    }
  }, []);

  const fetchFilterDropDown = useCallback(async (query) => {
    try {
      const response = await ManageApis(
        "get",
        `${global.getHeadlessAppFilterDropDown}?query=${query}`,
      );
      if (response?.message === "Success") {
        const mapped = (response?.response_data || []).map((item) => ({
          key: item,
          value: item,
        }));
        if (query === "source") setSourceList(mapped);
        else if (query === "doctype") setDocTypeList(mapped);
        else if (query === "status") setStatusList(mapped);
      } else {
        Message.error(
          response?.message || HEADLESS_APP_LABELS?.errorFilterFetch,
        );
      }
    } catch (error) {
      console.error(HEADLESS_APP_LABELS?.errorFilterFetch2, error);
    }
  }, []);

  const fetchSummary = useCallback(async (filters, pagination) => {
    setIsLoading(true);
    try {
      const updatedFilters = applyClientId(filters, user_Roll);
      const params = {
        ...updatedFilters,
        page: pagination?.current,
        pageSize: pagination?.pageSize,
      };

      const query = buildQueryParams(params);

      const res = await ManageApis(
        "get",
        `${global.getHeadlessAppHitList}?${query}`,
      );

      if (res.message === "Success") {
        const hits = res?.response_data?.data?.hits || [];
        const totalCount = res?.response_data?.data?.totalCount || 0;
        setTableData(hits);
        setTotal(totalCount);
      } else {
        Message.error(res?.message || HEADLESS_APP_LABELS?.errorDataFetch);
        setTableData([]);
        setTotal(0);
      }
    } catch (err) {
      console.error(HEADLESS_APP_LABELS?.errorSummaryFetch, err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const downloadSummary = useCallback(async (filters) => {
    try {
      const updatedFilters = applyClientId(filters, user_Roll);
      const query = buildQueryParams(updatedFilters);

      const res = await axios.get(
        `${global.uploadData + global.getHeadlessAppExport}?${query}`,
        { responseType: "blob" },
      );

      if (res.status === 200) {
        const filename = `ExportAppSummaryLogs_${Date.now()}.xlsx`;
        const link = document.createElement("a");
        link.href = window.URL.createObjectURL(res.data);
        link.download = filename;
        link.click();
        link.remove();
      } else {
        Message.error(HEADLESS_APP_LABELS?.errorDownload);
      }
    } catch (err) {
      console.error(HEADLESS_APP_LABELS?.errorDownload2, err);
    }
  }, []);

  useEffect(() => {
    fetchDeviceList();
    getAccountList();
    fetchFilterDropDown("source");
    fetchFilterDropDown("doctype");
    fetchFilterDropDown("status");
  }, [fetchDeviceList, getAccountList, fetchFilterDropDown]);

  return {
    deviceList,
    accountList,
    sourceList,
    docTypeList,
    statusList,
    tableData,
    total,
    isLoading,
    fetchSummary,
    downloadSummary,
  };
}
